import React, { useCallback, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useConversation } from '@elevenlabs/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';

// Convert markdown to HTML
const markdownToHtml = (text) => {
  if (!text) return '';
  
  // Clean up markdown code blocks if present (sometimes the LLM wraps the response in ```markdown ... ```)
  let cleanText = text.trim();
  if (cleanText.startsWith('```markdown')) {
    cleanText = cleanText.replace(/^```markdown\s*/i, '');
    cleanText = cleanText.replace(/```\s*$/i, '');
  } else if (cleanText.startsWith('```')) {
    cleanText = cleanText.replace(/^```\w*\s*/i, '');
    cleanText = cleanText.replace(/```\s*$/i, '');
  }
  
  // Split into lines for processing
  const lines = cleanText.split('\n');
  let html = '';
  let inList = false;
  let listType = null; // 'ul' or 'ol'
  let listItems = [];
  let listIndent = 0;
  
  const closeList = () => {
    if (listItems.length > 0) {
      const listTag = listType === 'ol' ? 'ol' : 'ul';
      const listClass = listType === 'ol' 
        ? 'list-decimal list-inside my-3 space-y-1 ml-4' 
        : 'list-disc list-inside my-3 space-y-1 ml-4';
      html += `<${listTag} class="${listClass}">${listItems.join('')}</${listTag}>`;
      listItems = [];
      inList = false;
      listType = null;
      listIndent = 0;
    }
  };
  
  const processBold = (line) => {
    // Handle **bold** text
    return line.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold">$1</strong>');
  };
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const leadingSpaces = line.length - line.trimStart().length;
    
    // Check for markdown headers (# ## ###)
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      closeList();
      const level = headerMatch[1].length;
      const headerText = processBold(headerMatch[2]);
      const headerClass = level === 1 
        ? 'font-bold text-2xl mt-6 mb-3' 
        : level === 2 
        ? 'font-bold text-xl mt-5 mb-2' 
        : 'font-bold text-lg mt-4 mb-2';
      html += `<h${level} class="${headerClass}">${headerText}</h${level}>`;
      return;
    }
    
    // Check for numbered list (1. 2. 3.)
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      const currentIndent = Math.floor(leadingSpaces / 2);
      if (!inList || listType !== 'ol' || currentIndent !== listIndent) {
        closeList();
        inList = true;
        listType = 'ol';
        listIndent = currentIndent;
      }
      listItems.push(`<li class="my-1">${processBold(numberedMatch[2])}</li>`);
      return;
    }
    
    // Check for bullet list (- item or * item)
    const bulletMatch = trimmed.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      const currentIndent = Math.floor(leadingSpaces / 2);
      if (!inList || listType !== 'ul' || currentIndent !== listIndent) {
        closeList();
        inList = true;
        listType = 'ul';
        listIndent = currentIndent;
      }
      // Handle nested lists with indentation
      const indentClass = currentIndent > 0 ? `ml-${currentIndent * 4}` : '';
      listItems.push(`<li class="my-1 ${indentClass}">${processBold(bulletMatch[1])}</li>`);
      return;
    }
    
    // Close list if we hit a non-list line
    if (inList) {
      closeList();
    }
    
    // Empty line
    if (!trimmed) {
      html += '<br>';
      return;
    }
    
    // Regular paragraph
    const processed = processBold(trimmed);
    html += `<p class="my-2">${processed}</p>`;
  });
  
  // Close any remaining list
  closeList();
  
  return html;
};

const ElevenLabsAgent = ({ onClose, onResponseReceived, onContactClick }) => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [webhookResponse, setWebhookResponse] = useState(null);
  const [showResponseWindow, setShowResponseWindow] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const cancelWebhookRef = useRef(false);
  const isConnectedRef = useRef(false);
  const webhookCalledRef = useRef(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [webhook404Error, setWebhook404Error] = useState(null);

  // Get agent ID from environment variables
  const agentId = import.meta.env?.VITE_ELEVENLABS_AGENT_ID;

  // Webhook URL for conversation end
  const webhookUrl = 'https://n8n.wigbers-automation.de/webhook/a17c6bd0-e6c9-45df-ac2a-8844bed75b9b';
  
  // Webhook URL for email submission
  const emailWebhookUrl = 'https://n8n.wigbers-automation.de/webhook/9cbe7449-2ee2-47db-8dd8-ee90e07bca64/';

  // Call webhook when conversation ends (defined before useConversation so it can be used in callbacks)
  const callWebhook = useCallback(async () => {
    // Prevent duplicate webhook calls
    if (webhookCalledRef.current) {
      console.log('⚠️ Webhook already called, skipping duplicate call');
      return;
    }
    
    webhookCalledRef.current = true;
    setIsLoadingResponse(true);
    setWebhook404Error(null); // Reset 404 error
    cancelWebhookRef.current = false; // Reset cancel flag
    const maxRetries = 20;
    const retryDelay = 3000; // 2 seconds
    const conversationId = localStorage.getItem('elevenlabs_conversation_id');
    
    if (!conversationId) {
      console.warn('⚠️ No conversation_id found in localStorage, webhook will be called without it');
    }
    
    try {
      let attempt = 1;
      let response;
      
      while (attempt <= maxRetries + 1) {
        // Check if cancelled
        if (cancelWebhookRef.current) {
          console.log('❌ Webhook call cancelled by user');
          setIsLoadingResponse(false);
          return;
        }
        
        console.log(`Calling webhook with conversation_id: ${conversationId} (Attempt ${attempt}/${maxRetries + 1})`);
        
        response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            conversation_id: conversationId || null,
          }),
        });
        
        // If we get a 202, retry after delay (unless we've reached max retries)
        if (response.status === 202) {
          if (attempt <= maxRetries) {
            console.log(`⏳ Received 202 Accepted, retrying in ${retryDelay / 1000} seconds... (${attempt}/${maxRetries})`);
            
            // Wait with cancellation check
            for (let i = 0; i < retryDelay; i += 100) {
              if (cancelWebhookRef.current) {
                console.log('❌ Webhook call cancelled during retry delay');
                setIsLoadingResponse(false);
                return;
              }
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            attempt++;
            continue; // Retry
          } else {
            console.error('❌ Max retries reached (10) for 202 status');
            setIsLoadingResponse(false);
            return;
          }
        }
        
        // Check if cancelled before processing response
        if (cancelWebhookRef.current) {
          console.log('❌ Webhook call cancelled by user');
          setIsLoadingResponse(false);
          return;
        }
        
        // If response is OK, process it
        if (response.ok) {
          const responseData = await response.json();
          console.log('✅ Webhook called successfully with conversation_id:', conversationId);
          console.log('Webhook response:', responseData);
          
          // Check again after async operation
          if (cancelWebhookRef.current) {
            console.log('❌ Webhook call cancelled after response received');
            setIsLoadingResponse(false);
            return;
          }
          
          // Extract the actual response text (handle nested structure)
          let responseText = null;
          let convId = null;
          
          // Try to find response in various possible structures
          if (responseData?.response) {
            responseText = responseData.response;
            convId = responseData.conversation_id;
          } else if (typeof responseData === 'object') {
            // Search recursively for response field
            const findResponse = (obj) => {
              if (obj?.response && typeof obj.response === 'string') {
                return { response: obj.response, conversation_id: obj.conversation_id };
              }
              for (const key in obj) {
                if (typeof obj[key] === 'object' && obj[key] !== null) {
                  const found = findResponse(obj[key]);
                  if (found) return found;
                }
              }
              return null;
            };
            const found = findResponse(responseData);
            if (found) {
              responseText = found.response;
              convId = found.conversation_id;
            }
          }
          
          // Create normalized response object
          const actualResponse = {
            response: responseText || responseData?.response || 'No response available',
            conversation_id: convId || responseData?.conversation_id || conversationId,
            id: responseData?.id,
            createdAt: responseData?.createdAt,
            updatedAt: responseData?.updatedAt
          };
          
        // Store the response and show the modal
        setWebhookResponse(actualResponse);
        setShowResponseWindow(true);
        
        // Pass response to parent component if callback provided
        if (onResponseReceived) {
          onResponseReceived(actualResponse);
        }
          
          setIsLoadingResponse(false);
          return; // Success, exit
        } else {
          // Non-202 error status
          console.error('❌ Webhook call failed with status:', response.status);
          const errorText = await response.text();
          console.error('Error response:', errorText);
          
          // Check specifically for 404 status
          if (response.status === 404) {
            setWebhook404Error('Der Anruf war möglicherweise zu kurz, der Use-Case konnte nicht analysiert werden oder es ist ein anderes Problem aufgetreten.');
          }
          
          setIsLoadingResponse(false);
          return;
        }
      }
    } catch (error) {
      if (!cancelWebhookRef.current) {
        console.error('❌ Error calling webhook:', error);
      }
      setIsLoadingResponse(false);
    }
  }, [webhookUrl, onResponseReceived]);

  // Cancel webhook generation
  const cancelGeneration = useCallback(() => {
    cancelWebhookRef.current = true;
    setIsLoadingResponse(false);
    console.log('✅ Generation cancelled by user');
  }, []);

  // Validate email
  const validateEmail = (emailValue) => {
    if (!emailValue.trim()) {
      return 'E-Mail-Adresse ist erforderlich';
    }
    if (!/\S+@\S+\.\S+/.test(emailValue)) {
      return 'Ungültige E-Mail-Adresse';
    }
    return '';
  };

  // Submit email to webhook
  const submitEmail = useCallback(async () => {
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setEmailError('');
    setIsSubmittingEmail(true);

    try {
      const conversationId = localStorage.getItem('elevenlabs_conversation_id') || webhookResponse?.conversation_id;
      
      const response = await fetch(emailWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          conversation_id: conversationId || null,
        }),
      });

      if (response.ok) {
        setEmailSubmitted(true);
        console.log('✅ Email submitted successfully');
      } else {
        const errorText = await response.text();
        console.error('❌ Email submission failed:', response.status, errorText);
        setEmailError('Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('❌ Error submitting email:', error);
      setEmailError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmittingEmail(false);
    }
  }, [email, emailWebhookUrl, webhookResponse]);

  const conversation = useConversation({
    onMessage: (message) => {
      console.log('Agent message:', message);
    },
    onConnect: () => {
      console.log('✅ Connected to agent');
      setIsConnected(true);
      isConnectedRef.current = true;
      setIsConnecting(false);
    },
    onStatusChange: (status) => {
      // status: 'connected' | 'connecting' | 'disconnected'
      console.log('📊 Status changed:', status);
      if (status === 'connected') {
        setIsConnected(true);
        isConnectedRef.current = true;
        setIsConnecting(false);
      } else if (status === 'connecting') {
        setIsConnecting(true);
      } else if (status === 'disconnected') {
        console.log('🔄 Status: disconnected');
        if (isConnectedRef.current) {
          // Only call webhook if we were previously connected (not just a failed connection)
          console.log('🔄 Agent hung up / session ended (from onStatusChange)');
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsConnecting(false);
          callWebhook();
        }
      }
    },
    onDisconnect: () => {
      console.log('🔄 Agent hung up / session ended (from onDisconnect)');
      if (isConnectedRef.current) {
        setIsConnected(false);
        isConnectedRef.current = false;
        setIsConnecting(false);
        // Call webhook when agent hangs up
        callWebhook();
      }
    },
    onError: (error) => {
      console.error('Conversation error:', error);
      
      // Check if error indicates the call was ended (by bot or connection loss)
      const errorMessage = error?.message || error?.toString() || '';
      const isCallEnded = errorMessage.includes('ended') || 
                         errorMessage.includes('disconnected') ||
                         errorMessage.includes('closed') ||
                         errorMessage.includes('terminated');
      
      if (isCallEnded && isConnectedRef.current) {
        console.log('🔄 Call ended by bot or connection loss, calling webhook...');
        setIsConnected(false);
        isConnectedRef.current = false;
        setIsConnecting(false);
        // Call webhook when call ends unexpectedly
        callWebhook();
      } else {
        setPermissionError('Fehler bei der Verbindung. Bitte versuchen Sie es erneut.');
        setIsConnecting(false);
        // Don't set isConnected to false here - let the user retry
      }
    },
    onEvent: (event) => {
      console.log('Conversation event received:', event);
      console.log('Event type:', event?.type);
      console.log('Full event structure:', JSON.stringify(event, null, 2));
      
      // Handle conversation_initiation_metadata event
      // Structure: {"conversation_initiation_metadata_event":{"conversation_id":"...",...},"type":"conversation_initiation_metadata"}
      if (event?.type === 'conversation_initiation_metadata' && 
          event?.conversation_initiation_metadata_event?.conversation_id) {
        const conversationId = event.conversation_initiation_metadata_event.conversation_id;
        localStorage.setItem('elevenlabs_conversation_id', conversationId);
        console.log('✅ Conversation ID stored from onEvent:', conversationId);
      }
      
      // Handle conversation end events (when bot ends the call)
      const endEventTypes = [
        'conversation_end',
        'conversation_ended',
        'session_end',
        'session_ended',
        'call_end',
        'call_ended',
        'conversation_terminated'
      ];
      
      if (endEventTypes.includes(event?.type) && isConnectedRef.current) {
        console.log('🔄 Conversation ended by bot/agent, event type:', event?.type);
        setIsConnected(false);
        isConnectedRef.current = false;
        setIsConnecting(false);
        // Call webhook when bot ends the call
        callWebhook();
      }
      
      // Also check for nested event structures that might indicate end
      if (event?.conversation_end_event || event?.session_end_event || event?.call_end_event) {
        console.log('🔄 Conversation ended by bot/agent (nested event structure)');
        if (isConnectedRef.current) {
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsConnecting(false);
          callWebhook();
        }
      }
    },
  });

  // Request microphone permission
  const requestMicrophonePermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasPermission(true);
      setPermissionError(null);
      return true;
    } catch (error) {
      console.error('Microphone permission error:', error);
      setPermissionError('Mikrofon-Zugriff ist erforderlich, um die Konversation zu starten.');
      return false;
    }
  }, []);

  // Start conversation session
  const startConversation = useCallback(async () => {
    if (!agentId) {
      setPermissionError('Agent-ID ist nicht konfiguriert. Bitte kontaktieren Sie den Administrator.');
      return;
    }

    // Don't start if already connected
    if (isConnected) {
      console.log('Conversation already connected, skipping start');
      return;
    }

    setIsConnecting(true);
    setPermissionError(null);
    setWebhook404Error(null); // Reset 404 error when starting new conversation

    try {
      // Request permission first if not already granted
      if (!hasPermission) {
        const permissionGranted = await requestMicrophonePermission();
        if (!permissionGranted) {
          setIsConnecting(false);
          return;
        }
      }

      // Start the conversation with agent ID
      console.log('Starting conversation with agentId:', agentId);
      const sessionResult = await conversation.startSession({
        agentId: agentId,
      });
      console.log('Conversation started successfully, session result:', sessionResult);
      
      // The sessionResult is the conversation_id directly (as a string)
      // Store it in localStorage
      if (sessionResult) {
        const conversationId = typeof sessionResult === 'string' 
          ? sessionResult 
          : (sessionResult?.conversation_id || sessionResult?.conversationId || sessionResult);
        
        if (conversationId) {
          localStorage.setItem('elevenlabs_conversation_id', conversationId);
          console.log('✅ Conversation ID stored from startSession:', conversationId);
        }
      }
      
      // Also check if conversation object has conversationId property (fallback)
      if (conversation?.conversationId || conversation?.conversation_id) {
        const conversationId = conversation.conversationId || conversation.conversation_id;
        localStorage.setItem('elevenlabs_conversation_id', conversationId);
        console.log('✅ Conversation ID stored from conversation object:', conversationId);
      }
      
      setIsConnected(true);
      isConnectedRef.current = true;
      webhookCalledRef.current = false; // Reset webhook flag when starting new conversation
      setIsConnecting(false);
    } catch (error) {
      console.error('Error starting conversation:', error);
      setPermissionError('Fehler beim Starten der Konversation. Bitte versuchen Sie es erneut.');
      setIsConnecting(false);
      setIsConnected(false);
      isConnectedRef.current = false;
    }
  }, [hasPermission, requestMicrophonePermission, conversation, agentId, isConnected]);


  // Stop conversation
  const stopConversation = useCallback(async () => {
    try {
      // Log available methods for debugging
      console.log('Conversation object methods:', Object.keys(conversation));
      
      // Use endSession instead of stopSession
      if (typeof conversation.endSession === 'function') {
        await conversation.endSession();
        console.log('Conversation ended using endSession');
      } else if (typeof conversation.stopSession === 'function') {
        await conversation.stopSession();
        console.log('Conversation ended using stopSession');
      } else {
        console.warn('No endSession or stopSession method found on conversation object');
      }
      setIsConnected(false);
      isConnectedRef.current = false;
    } catch (error) {
      console.error('Error stopping conversation:', error);
      setIsConnected(false);
      isConnectedRef.current = false;
    } finally {
      // Always call webhook when conversation ends, regardless of errors
      await callWebhook();
    }
  }, [conversation, callWebhook]);

  // Listen for conversation_initiation_metadata event via realtime client
  useEffect(() => {
    // Access the real-time client from the conversation object
    const realtimeClient = conversation?.realtimeClient;
    
    if (realtimeClient) {
      console.log('Realtime client found, setting up event listeners');
      
      const handleEvent = (event) => {
        console.log('Realtime client event received:', event);
        
        // Parse if event is a string
        if (typeof event === 'string') {
          try {
            event = JSON.parse(event);
          } catch (e) {
            console.warn('Could not parse event as JSON:', e);
            return;
          }
        }
        
        // Handle the exact structure: {"conversation_initiation_metadata_event":{"conversation_id":"...",...},"type":"conversation_initiation_metadata"}
        if (event?.type === 'conversation_initiation_metadata' && 
            event?.conversation_initiation_metadata_event?.conversation_id) {
          const conversationId = event.conversation_initiation_metadata_event.conversation_id;
          localStorage.setItem('elevenlabs_conversation_id', conversationId);
          console.log('✅ Conversation ID stored from realtime client:', conversationId);
        }
        
        // Handle conversation end events (when bot ends the call)
        const endEventTypes = [
          'conversation_end',
          'conversation_ended',
          'session_end',
          'session_ended',
          'call_end',
          'call_ended',
          'conversation_terminated'
        ];
        
        if (endEventTypes.includes(event?.type) && isConnectedRef.current) {
          console.log('🔄 Conversation ended by bot/agent (realtime client), event type:', event?.type);
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsConnecting(false);
          // Call webhook when bot ends the call
          callWebhook();
        }
        
        // Also check for nested event structures that might indicate end
        if (event?.conversation_end_event || event?.session_end_event || event?.call_end_event) {
          console.log('🔄 Conversation ended by bot/agent (realtime client, nested event structure)');
          if (isConnectedRef.current) {
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        }
      };
      
      const handleMessage = (message) => {
        console.log('Realtime client message received:', message);
        
        // Parse if message is a string
        if (typeof message === 'string') {
          try {
            message = JSON.parse(message);
          } catch (e) {
            console.warn('Could not parse message as JSON:', e);
            return;
          }
        }
        
        // Handle the exact structure
        if (message?.type === 'conversation_initiation_metadata' && 
            message?.conversation_initiation_metadata_event?.conversation_id) {
          const conversationId = message.conversation_initiation_metadata_event.conversation_id;
          localStorage.setItem('elevenlabs_conversation_id', conversationId);
          console.log('✅ Conversation ID stored from realtime client message:', conversationId);
        }
        
        // Handle conversation end events (when bot ends the call)
        const endEventTypes = [
          'conversation_end',
          'conversation_ended',
          'session_end',
          'session_ended',
          'call_end',
          'call_ended',
          'conversation_terminated'
        ];
        
        if (endEventTypes.includes(message?.type) && isConnectedRef.current) {
          console.log('🔄 Conversation ended by bot/agent (realtime client message), event type:', message?.type);
          setIsConnected(false);
          isConnectedRef.current = false;
          setIsConnecting(false);
          // Call webhook when bot ends the call
          callWebhook();
        }
        
        // Also check for nested event structures that might indicate end
        if (message?.conversation_end_event || message?.session_end_event || message?.call_end_event) {
          console.log('🔄 Conversation ended by bot/agent (realtime client message, nested event structure)');
          if (isConnectedRef.current) {
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        }
      };

      // Try different event listener methods
      if (typeof realtimeClient.on === 'function') {
        // Listen for the specific event type
        realtimeClient.on('conversation_initiation_metadata', handleEvent);
        // Listen for all messages and check for the event
        realtimeClient.on('message', handleMessage);
        // Listen to all events as fallback
        realtimeClient.on('*', (event) => {
          console.log('Realtime client * event:', event);
          if (event?.type === 'conversation_initiation_metadata') {
            handleEvent(event);
          }
          // Also handle conversation end events through the wildcard listener
          const endEventTypes = [
            'conversation_end',
            'conversation_ended',
            'session_end',
            'session_ended',
            'call_end',
            'call_ended',
            'conversation_terminated'
          ];
          if (endEventTypes.includes(event?.type) && isConnectedRef.current) {
            handleEvent(event);
          }
        });
        // Also listen for data events
        realtimeClient.on('data', (data) => {
          console.log('Realtime client data received:', data);
          handleMessage(data);
        });
        
        // Listen for close/disconnect events (when agent closes the connection)
        realtimeClient.on('close', () => {
          console.log('🔄 Realtime client closed');
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client closed while connected - triggering webhook (bot hung up)');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
        
        realtimeClient.on('disconnect', () => {
          console.log('🔄 Realtime client disconnected');
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client disconnected while connected - triggering webhook (bot hung up)');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
        
        realtimeClient.on('error', (error) => {
          console.log('🔄 Realtime client error:', error);
          // If we were connected and get an error, treat it as connection loss
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client error while connected - triggering webhook');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
      } else if (typeof realtimeClient.addEventListener === 'function') {
        realtimeClient.addEventListener('conversation_initiation_metadata', handleEvent);
        realtimeClient.addEventListener('message', handleMessage);
        
        // Listen for close/disconnect events
        realtimeClient.addEventListener('close', () => {
          console.log('🔄 Realtime client closed');
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client closed while connected - triggering webhook (bot hung up)');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
        
        realtimeClient.addEventListener('disconnect', () => {
          console.log('🔄 Realtime client disconnected');
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client disconnected while connected - triggering webhook (bot hung up)');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
        
        realtimeClient.addEventListener('error', (error) => {
          console.log('🔄 Realtime client error:', error);
          if (isConnectedRef.current) {
            console.log('🔄 Realtime client error while connected - triggering webhook');
            setIsConnected(false);
            isConnectedRef.current = false;
            setIsConnecting(false);
            callWebhook();
          }
        });
      }
      
      // Also try to access the WebSocket directly if available
      if (realtimeClient?.ws || realtimeClient?.socket) {
        const ws = realtimeClient.ws || realtimeClient.socket;
        if (ws && typeof ws.addEventListener === 'function') {
          ws.addEventListener('message', (wsEvent) => {
            try {
              const data = JSON.parse(wsEvent.data);
              console.log('WebSocket message received:', data);
              if (data?.type === 'conversation_initiation_metadata' && 
                  data?.conversation_initiation_metadata_event?.conversation_id) {
                const conversationId = data.conversation_initiation_metadata_event.conversation_id;
                localStorage.setItem('elevenlabs_conversation_id', conversationId);
                console.log('✅ Conversation ID stored from WebSocket:', conversationId);
              }
              
              // Handle conversation end events from WebSocket
              const endEventTypes = [
                'conversation_end',
                'conversation_ended',
                'session_end',
                'session_ended',
                'call_end',
                'call_ended',
                'conversation_terminated'
              ];
              
              if (endEventTypes.includes(data?.type) && isConnectedRef.current) {
                console.log('🔄 Conversation ended by bot/agent (WebSocket), event type:', data?.type);
                setIsConnected(false);
                isConnectedRef.current = false;
                setIsConnecting(false);
                callWebhook();
              }
            } catch (e) {
              console.warn('Could not parse WebSocket message:', e);
            }
          });
          
          // Listen for WebSocket close event (when agent closes the connection)
          ws.addEventListener('close', (closeEvent) => {
            console.log('🔄 WebSocket closed:', closeEvent.code, closeEvent.reason);
            // Only trigger webhook if we were connected (not if it was a failed connection)
            if (isConnectedRef.current) {
              console.log('🔄 WebSocket closed while connected - triggering webhook (bot hung up)');
              setIsConnected(false);
              isConnectedRef.current = false;
              setIsConnecting(false);
              // Call webhook exactly like when user clicks "Anruf beenden"
              callWebhook();
            }
          });
          
          // Also listen for error events that might indicate connection loss
          ws.addEventListener('error', (errorEvent) => {
            console.log('🔄 WebSocket error:', errorEvent);
            // If we were connected and get an error, treat it as connection loss
            if (isConnectedRef.current) {
              console.log('🔄 WebSocket error while connected - triggering webhook');
              setIsConnected(false);
              isConnectedRef.current = false;
              setIsConnecting(false);
              callWebhook();
            }
          });
        }
      }

      // Cleanup
      return () => {
        if (typeof realtimeClient.off === 'function') {
          realtimeClient.off('conversation_initiation_metadata', handleEvent);
          realtimeClient.off('close');
          realtimeClient.off('disconnect');
          realtimeClient.off('error');
        } else if (typeof realtimeClient.removeEventListener === 'function') {
          realtimeClient.removeEventListener('conversation_initiation_metadata', handleEvent);
          realtimeClient.removeEventListener('close');
          realtimeClient.removeEventListener('disconnect');
          realtimeClient.removeEventListener('error');
        }
      };
    } else {
      console.log('Realtime client not available yet');
    }
  }, [conversation]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      // Only cleanup on unmount, not when isConnected changes
      if (isConnected) {
        // Call webhook on unmount
        callWebhook();
        // Try to end session if possible
        try {
          if (typeof conversation?.endSession === 'function') {
            conversation.endSession();
          } else if (typeof conversation?.stopSession === 'function') {
            conversation.stopSession();
          }
        } catch (error) {
          console.error('Error ending conversation on unmount:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Auto-start conversation when component mounts
  useEffect(() => {
    if (agentId) {
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - we intentionally don't include startConversation to avoid re-triggering

  return (
    <>
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-primary to-brand-purple rounded-full flex items-center justify-center">
              <Icon name="Phone" size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">Voice-Agent</h3>
              <p className="text-xs text-text-secondary">Stefans Assistent</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={18} className="text-text-secondary" />
          </motion.button>
        </div>

        {/* Status/Error Messages */}
        <AnimatePresence>
          {permissionError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <p className="text-sm text-red-800">{permissionError}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Connection Status */}
        <div className="flex flex-col items-center justify-center min-h-[200px] space-y-4">
          {isConnecting && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center space-y-4"
            >
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="Phone" size={24} className="text-primary" />
                </div>
              </div>
              <p className="text-sm text-text-secondary">Verbinde mit Agent...</p>
            </motion.div>
          )}

          {isConnected && !isConnecting && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center space-y-4 w-full"
            >
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg"
              >
                <Icon name="Phone" size={32} className="text-white" />
              </motion.div>
              <div className="text-center">
                <p className="text-sm font-semibold text-text-primary mb-1">Verbunden</p>
                <p className="text-xs text-text-secondary">Sie können jetzt sprechen</p>
              </div>
              
              {/* Stop Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  stopConversation();
                  // Don't call onClose() here - keep section open to show loading and response
                }}
                className="mt-4 px-6 py-2 bg-gradient-to-r from-primary to-brand-purple text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <Icon name="X" size={18} className="text-white" />
                <span className="text-white">Anruf beenden</span>
              </motion.button>
            </motion.div>
          )}

          {!isConnecting && !isConnected && !isLoadingResponse && !showResponseWindow && !permissionError && !webhook404Error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center space-y-4"
            >
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Phone" size={24} className="text-primary" />
              </div>
              <p className="text-sm text-text-secondary text-center">
                Bereit zum Starten
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startConversation}
                className="px-6 py-2 bg-gradient-to-r from-primary to-brand-purple text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <Icon name="Phone" size={18} />
                <span>Anruf starten</span>
              </motion.button>
            </motion.div>
          )}

          {/* Show message when call ended and waiting for response */}
          {!isConnecting && !isConnected && isLoadingResponse && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center space-y-4"
            >
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Icon name="Phone" size={24} className="text-primary" />
                </div>
              </div>
              <p className="text-sm text-text-secondary">Anruf beendet. Usecase wird generiert...</p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={cancelGeneration}
                className="mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-text-secondary rounded-lg font-medium transition-colors flex items-center space-x-2 text-sm"
              >
                <Icon name="X" size={16} className="text-text-secondary" />
                <span>Abbrechen</span>
              </motion.button>
            </motion.div>
          )}

          {/* Show 404 error message */}
          {!isConnecting && !isConnected && !isLoadingResponse && webhook404Error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col items-center space-y-4 w-full"
            >
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="w-full p-4 bg-amber-50 border border-amber-200 rounded-lg"
              >
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{webhook404Error}</p>
                </div>
              </motion.div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startConversation}
                className="px-6 py-2 bg-gradient-to-r from-primary to-brand-purple text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <Icon name="Phone" size={18} className="text-white" />
                <span className="text-white">Erneut versuchen</span>
              </motion.button>
            </motion.div>
          )}
        </div>

        {/* Info Text */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-text-secondary text-center">
            Der Voice-Agent sammelt automatisch Ihren Use-Case. Erklären Sie einfach Ihre Anforderungen.
          </p>
        </div>

      </div>
    </div>

    {/* Agent Response Modal - Rendered via Portal to escape stacking contexts */}
    {createPortal(
      <AnimatePresence>
        {showResponseWindow && webhookResponse && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 md:p-8">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => {
                setShowResponseWindow(false);
                if (onClose) {
                  onClose();
                }
              }}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative z-[10000] flex flex-col w-full max-w-4xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-4rem)] overflow-hidden text-left bg-white shadow-xl rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header - Fixed */}
              <div className="flex-shrink-0 relative bg-gradient-to-br from-primary/10 via-brand-purple/10 to-primary/10 p-4 sm:p-6 border-b border-gray-200">
                <button
                  onClick={() => {
                    setShowResponseWindow(false);
                    if (onClose) {
                      onClose();
                    }
                  }}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors z-10"
                  aria-label="Close modal"
                >
                  <Icon name="X" size={20} />
                </button>
                <div className="flex items-center space-x-3 pr-12">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary to-brand-purple rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={20} className="text-white sm:hidden" />
                    <Icon name="FileText" size={24} className="text-white hidden sm:block" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-text-primary">Use-Case Zusammenfassung</h2>
                    <p className="text-xs sm:text-sm text-text-secondary">Ihr Use-Case wurde analysiert</p>
                  </div>
                </div>
              </div>

              {/* Email Submission Section - Fixed */}
              <div className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-primary/5 via-brand-purple/5 to-primary/5 border-b border-gray-200">
                {!emailSubmitted ? (
                  <div className="flex flex-col gap-2 sm:gap-3">
                    <Input
                      label="E-Mail-Adresse"
                      type="email"
                      placeholder="ihre.email@unternehmen.de"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) {
                          setEmailError('');
                        }
                      }}
                      error={emailError}
                      required
                      className="w-full"
                    />
                    <Button
                      variant="default"
                      size="lg"
                      iconName="Mail"
                      iconPosition="left"
                      onClick={submitEmail}
                      loading={isSubmittingEmail}
                      disabled={isSubmittingEmail || !email.trim()}
                      className="w-full conversion-pulse"
                    >
                      {isSubmittingEmail ? 'Wird gesendet...' : 'unverbindliches Angebot + ROI-Report erhalten'}
                    </Button>
                    <p className="text-xs text-text-secondary text-center">
                      Geben Sie Ihre E-Mail-Adresse ein, um das Angebot und den ROI-Report zu erhalten
                    </p>
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg text-center"
                  >
                    <div className="flex items-center justify-center mb-2">
                      <Icon name="CheckCircle" size={24} className="text-green-600" />
                    </div>
                    <p className="text-sm font-semibold text-green-800 mb-1">
                      E-Mail erfolgreich gesendet!
                    </p>
                    <p className="text-xs text-green-700">
                      Das Angebot und der ROI-Report wurden an {email} gesendet.
                    </p>
                  </motion.div>
                )}
              </div>

              {/* Content - Scrollable */}
              <div className="flex-1 min-h-0 p-4 sm:p-6 overflow-y-auto">
                <div className="prose prose-sm max-w-none">
                  <div 
                    className="text-sm text-text-primary leading-relaxed prose-headings:font-bold prose-p:my-2 prose-strong:font-bold prose-ul:my-2 prose-li:my-1"
                    dangerouslySetInnerHTML={{ 
                      __html: markdownToHtml(webhookResponse.response || 'No response text available')
                    }}
                  />
                </div>
              </div>

              {/* Footer with Additional CTA Buttons - Fixed */}
              <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200">
                  {/* Additional CTA Buttons */}
                  <div className="flex flex-col gap-3 items-center">
                    <Button
                      variant="outline"
                      size="lg"
                      iconName="MessageCircle"
                      iconPosition="left"
                      onClick={() => {
                        setShowResponseWindow(false);
                        if (onClose) {
                          onClose();
                        }
                        // If onContactClick is provided (e.g., on agentops page), use it
                        // Otherwise, navigate to contact section
                        if (onContactClick) {
                          onContactClick();
                        } else {
                          navigate('/#kontakt');
                        }
                      }}
                      className="w-full sm:w-auto"
                    >
                      Kontakt aufnehmen
                    </Button>
                    <button
                      onClick={() => {
                        setShowResponseWindow(false);
                        if (onClose) {
                          onClose();
                        }
                      }}
                      className="text-sm text-text-secondary hover:text-text-primary transition-colors"
                    >
                      Schließen
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
        )}
      </AnimatePresence>,
      document.body
    )}
    </>
  );
};

export default ElevenLabsAgent;

